import { AppDispatch, RootState } from '../../../store'
import {
  CollectionCreationStep,
  CollectionType,
  EMPTY_COLLECTION_METRICS,
  TeamCollection,
} from './types'
import { DUMMY_SUGGESTED_MEMBER_IDS } from './dummyData'

export enum TeamRelationshipActionTypes {
  OPEN_CREATE_COLLECTION = 'TEAM_RELATIONSHIP_OPEN_CREATE_COLLECTION',
  CLOSE_CREATE_COLLECTION = 'TEAM_RELATIONSHIP_CLOSE_CREATE_COLLECTION',
  SET_CREATION_STEP = 'TEAM_RELATIONSHIP_SET_CREATION_STEP',
  SET_DRAFT_COLLECTION_TYPE = 'TEAM_RELATIONSHIP_SET_DRAFT_COLLECTION_TYPE',
  SET_DRAFT_COLLECTION_NAME = 'TEAM_RELATIONSHIP_SET_DRAFT_COLLECTION_NAME',
  SET_DRAFT_SELECTED_MEMBER_IDS = 'TEAM_RELATIONSHIP_SET_DRAFT_SELECTED_MEMBER_IDS',
  TOGGLE_DRAFT_MEMBER = 'TEAM_RELATIONSHIP_TOGGLE_DRAFT_MEMBER',
  ADD_COLLECTION = 'TEAM_RELATIONSHIP_ADD_COLLECTION',
  SET_COLLECTIONS = 'TEAM_RELATIONSHIP_SET_COLLECTIONS',
  SELECT_COLLECTION = 'TEAM_RELATIONSHIP_SELECT_COLLECTION',
  CLEAR_SELECTED_COLLECTION = 'TEAM_RELATIONSHIP_CLEAR_SELECTED_COLLECTION',
  RENAME_COLLECTION = 'TEAM_RELATIONSHIP_RENAME_COLLECTION',
  UPDATE_COLLECTION_MEMBER_IDS = 'TEAM_RELATIONSHIP_UPDATE_COLLECTION_MEMBER_IDS',
  SET_COLLECTION_ARCHIVED = 'TEAM_RELATIONSHIP_SET_COLLECTION_ARCHIVED',
  RESET_TEAM_RELATIONSHIP = 'TEAM_RELATIONSHIP_RESET',
}

export const openCreateCollection = () =>
  ({
    type: TeamRelationshipActionTypes.OPEN_CREATE_COLLECTION,
  }) as const

export const closeCreateCollection = () =>
  ({
    type: TeamRelationshipActionTypes.CLOSE_CREATE_COLLECTION,
  }) as const

export const setCreationStep = (step: CollectionCreationStep) =>
  ({
    type: TeamRelationshipActionTypes.SET_CREATION_STEP,
    payload: step,
  }) as const

export const setDraftCollectionType = (collectionType: CollectionType) =>
  ({
    type: TeamRelationshipActionTypes.SET_DRAFT_COLLECTION_TYPE,
    payload: collectionType,
  }) as const

export const setDraftCollectionName = (name: string) =>
  ({
    type: TeamRelationshipActionTypes.SET_DRAFT_COLLECTION_NAME,
    payload: name,
  }) as const

export const setDraftSelectedMemberIds = (memberIds: string[]) =>
  ({
    type: TeamRelationshipActionTypes.SET_DRAFT_SELECTED_MEMBER_IDS,
    payload: memberIds,
  }) as const

export const toggleDraftMember = (memberId: string) =>
  ({
    type: TeamRelationshipActionTypes.TOGGLE_DRAFT_MEMBER,
    payload: memberId,
  }) as const

export const addCollection = (collection: TeamCollection) =>
  ({
    type: TeamRelationshipActionTypes.ADD_COLLECTION,
    payload: collection,
  }) as const

export const setCollections = (collections: TeamCollection[]) =>
  ({
    type: TeamRelationshipActionTypes.SET_COLLECTIONS,
    payload: collections,
  }) as const

export const selectCollection = (collectionId: string) =>
  ({
    type: TeamRelationshipActionTypes.SELECT_COLLECTION,
    payload: collectionId,
  }) as const

export const clearSelectedCollection = () =>
  ({
    type: TeamRelationshipActionTypes.CLEAR_SELECTED_COLLECTION,
  }) as const

export const renameCollection = (collectionId: string, name: string) =>
  ({
    type: TeamRelationshipActionTypes.RENAME_COLLECTION,
    payload: { collectionId, name },
  }) as const

export const updateCollectionMemberIds = (
  collectionId: string,
  memberIds: string[]
) =>
  ({
    type: TeamRelationshipActionTypes.UPDATE_COLLECTION_MEMBER_IDS,
    payload: { collectionId, memberIds },
  }) as const

export const setCollectionArchived = (
  collectionId: string,
  archived: { isArchived: boolean }
) =>
  ({
    type: TeamRelationshipActionTypes.SET_COLLECTION_ARCHIVED,
    payload: { collectionId, isArchived: archived.isArchived },
  }) as const

export const resetTeamRelationship = () =>
  ({
    type: TeamRelationshipActionTypes.RESET_TEAM_RELATIONSHIP,
  }) as const

export const archiveSelectedCollection =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { selectedCollectionId, collectionsById } =
      getState().teamRelationship
    if (!selectedCollectionId || !collectionsById[selectedCollectionId]) {
      return
    }
    dispatch(setCollectionArchived(selectedCollectionId, { isArchived: true }))
    dispatch(clearSelectedCollection())
  }

export const unarchiveSelectedCollection =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { selectedCollectionId, collectionsById } =
      getState().teamRelationship
    if (!selectedCollectionId || !collectionsById[selectedCollectionId]) {
      return
    }
    dispatch(setCollectionArchived(selectedCollectionId, { isArchived: false }))
  }

export const continueFromCollectionType =
  (collectionType: CollectionType) => (dispatch: AppDispatch) => {
    dispatch(setDraftCollectionType(collectionType))
    dispatch(setCreationStep('nameCollection'))
  }

export const continueFromCollectionName =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { draft } = getState().teamRelationship
    if (!draft.name.trim()) {
      return
    }
    // Seed suggested AEs the first time we enter the members step.
    if (!draft.selectedMemberIds.length) {
      dispatch(setDraftSelectedMemberIds(DUMMY_SUGGESTED_MEMBER_IDS))
    }
    dispatch(setCreationStep('selectMembers'))
  }

export const goBackToCollectionTypeSelection =
  () => (dispatch: AppDispatch) => {
    dispatch(setCreationStep('selectType'))
  }

export const createCollectionId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `collection-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const resolveCreatedByName = (getState: () => RootState): string => {
  const userProfile = getState().userProfile?.userProfile
  if (!userProfile) {
    return 'Unknown'
  }
  const firstName = userProfile.givenName || userProfile.firstName || ''
  const lastName = userProfile.familyName || userProfile.lastName || ''
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()
  return fullName || userProfile.email || 'Unknown'
}

export const createCollectionFromDraft =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { draft } = getState().teamRelationship
    const name = draft.name.trim()
    if (!draft.type || !name) {
      return
    }

    const collection: TeamCollection = {
      id: createCollectionId(),
      name,
      type: draft.type,
      memberIds: [...draft.selectedMemberIds],
      createdAt: new Date().toISOString(),
      createdBy: resolveCreatedByName(getState),
      metrics: EMPTY_COLLECTION_METRICS,
      isArchived: false,
    }

    dispatch(addCollection(collection))
    dispatch(closeCreateCollection())
  }
